import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtError, fmtDateTime } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Approvals() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/reports/pending-approvals");
            setItems(data);
        } catch (e) { setItems([]); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const decide = async (id, decision, reason) => {
        try {
            await api.post(`/reports/${id}/decision`, { decision, reason });
            toast.success(`Report ${decision}`);
            load();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    };

    return (
        <div className="space-y-6">
            <header>
                <div className="overline mb-1">// Approval queue</div>
                <h1 className="font-display text-4xl tracking-tighter">Reports Awaiting Approval</h1>
                <p className="text-slate-500 mt-2 text-sm">Execution Head reviews reports before they're visible to clients.</p>
            </header>

            {loading && <div className="text-xs font-mono text-slate-400">loading…</div>}

            {!loading && items.length === 0 && (
                <div className="bg-white border border-slate-200 p-12 text-center">
                    <div className="overline mb-2">All clear</div>
                    <h2 className="font-display text-2xl">No reports pending</h2>
                    <p className="text-sm text-slate-500 mt-1">You're caught up. New submissions will appear here.</p>
                </div>
            )}

            <div className="space-y-3">
                {items.map((r) => (
                    <ApprovalRow key={r.id} report={r} onDecide={decide} />
                ))}
            </div>
        </div>
    );
}

function ApprovalRow({ report, onDecide }) {
    const [showReject, setShowReject] = useState(false);
    const [reason, setReason] = useState("");

    return (
        <div className="bg-white border border-slate-200 p-5" data-testid={`approval-row-${report.id}`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="overline">{report.kind} report</div>
                    <Link to={`/campaigns/${report.campaign_id}`} className="font-display text-lg hover:underline">
                        {report.campaign_name}
                    </Link>
                    <div className="text-xs font-mono text-slate-500 mt-1">
                        by {report.created_by_name} · {fmtDateTime(report.created_at)}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={() => setShowReject((s) => !s)}
                        variant="outline"
                        data-testid={`approval-reject-toggle-${report.id}`}
                        className="rounded-none border-slate-300 gap-2"
                    >
                        <XCircle size={14} /> Reject
                    </Button>
                    <Button
                        onClick={() => onDecide(report.id, "approved")}
                        data-testid={`approval-approve-${report.id}`}
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2"
                    >
                        <CheckCircle size={14} /> Approve & Publish
                    </Button>
                </div>
            </div>

            {showReject && (
                <div className="mt-4 border-t border-slate-200 pt-4 space-y-3">
                    <Textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why is this report being rejected?"
                        rows={2}
                        className="rounded-none border-slate-300"
                        data-testid={`approval-reject-reason-${report.id}`}
                    />
                    <div className="flex justify-end">
                        <Button
                            onClick={() => onDecide(report.id, "rejected", reason)}
                            data-testid={`approval-reject-confirm-${report.id}`}
                            className="rounded-none bg-red-700 hover:bg-red-800 text-white"
                        >
                            Confirm rejection
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
